import React, { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Minus, Plus, Trash2, ShoppingBasket, AlertTriangle, Gift, UserCircle2, LogIn, ArrowRight, Loader2 } from "lucide-react";
import CheckoutDialog from "./CheckoutDialog";
import RewardRedeemDialog from "./RewardRedeemDialog";
import { api } from "../lib/api";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function CartDrawer({ minOrder = 10, deliveryFee = 2, pastaFlavours, wingFlavours, onSignInClick }) {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, total: subtotalAll } = useCart();
  const { user, loyalty, refresh, login, register } = useAuth();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(true);
  const [rewardDialog, setRewardDialog] = useState({ open: false, reward: null });

  // Auth gate state
  const [gateTab, setGateTab] = useState("login");
  const [gateForm, setGateForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const setF = (k, v) => setGateForm((f) => ({ ...f, [k]: v }));

  const refreshStore = useCallback(async () => {
    try {
      const { data } = await api.get("/store/status");
      setStoreOpen(!!data.is_open);
    } catch {}
  }, []);

  useEffect(() => {
    if (drawerOpen) { refreshStore(); if (user) refresh(); }
  }, [drawerOpen, refreshStore, refresh, user]);

  const subtotalPaid = items.filter((i) => !i.reward_id).reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const belowMin = subtotalPaid < minOrder;
  const total = subtotalPaid + (subtotalPaid > 0 ? deliveryFee : 0);
  const blocked = items.length === 0 || belowMin || !storeOpen;

  let blockReason = "";
  if (!storeOpen) blockReason = "We're closed right now.";
  else if (belowMin) blockReason = `Add £${(minOrder - subtotalPaid).toFixed(2)} more to reach the £${minOrder.toFixed(2)} minimum.`;

  const usedRewardIds = new Set(items.filter((i) => i.reward_id).map((i) => i.reward_id));
  const availableRewards = (loyalty?.pending_rewards || []).filter((r) => !usedRewardIds.has(r.id));

  const handleCheckoutClick = () => {
    if (user) {
      setCheckoutOpen(true);
    } else {
      setGateOpen(true);
    }
  };

  const proceedAsGuest = () => {
    setGateOpen(false);
    setCheckoutOpen(true);
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setGateSubmitting(true);
    try {
      if (gateTab === "login") {
        await login(gateForm.email, gateForm.password);
        toast.success("Signed in");
      } else {
        if (!gateForm.name || !gateForm.phone) { toast.error("Name and phone are required"); setGateSubmitting(false); return; }
        await register({ name: gateForm.name, email: gateForm.email, phone: gateForm.phone, password: gateForm.password });
        toast.success("Account created — welcome!");
      }
      setGateOpen(false);
      setCheckoutOpen(true);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Auth failed");
    } finally {
      setGateSubmitting(false);
    }
  };

  return (
    <>
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="bg-black border-l-4 border-gold text-cream w-full sm:max-w-md p-0" data-testid="cart-drawer">
          <SheetHeader className="px-6 py-6 border-b border-border">
            <SheetTitle className="font-heading text-cream uppercase tracking-tight text-3xl flex items-center gap-3">
              <ShoppingBasket className="h-7 w-7 text-gold" /> Your Basket
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col h-[calc(100vh-100px)]">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {user && loyalty && (
                <div className="mb-4 border border-gold/40 p-3 bg-gold/5" data-testid="loyalty-summary">
                  <div className="font-heading uppercase tracking-widest text-gold text-xs">Loyalty</div>
                  <div className="text-cream text-sm mt-1">
                    <span className="font-heading text-2xl">{loyalty.order_count_cycle}</span>
                    <span className="text-cream/60"> / {loyalty.next_tier_target} to <span className="text-gold">{loyalty.next_tier_label}</span></span>
                  </div>
                  <div className="mt-2 h-2 bg-border">
                    <div
                      className="h-2 bg-gold transition-all"
                      style={{ width: `${Math.min(100, (loyalty.order_count_cycle / loyalty.next_tier_target) * 100)}%` }}
                      data-testid="loyalty-progress-bar"
                    />
                  </div>

                  {availableRewards.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {availableRewards.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => setRewardDialog({ open: true, reward: r })}
                          className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 bg-gold text-black font-heading uppercase tracking-widest text-sm hover:bg-amber-400 transition-colors"
                          data-testid={`apply-reward-${r.id}`}
                        >
                          <span className="inline-flex items-center gap-2"><Gift className="h-4 w-4" /> {r.label}</span>
                          <span>Use Now →</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!user && (
                <button
                  onClick={() => { setDrawerOpen(false); onSignInClick?.(); }}
                  className="mb-4 w-full px-3 py-2 border-2 border-gold/50 text-gold/90 font-heading uppercase tracking-widest text-xs hover:border-gold hover:text-gold transition-colors"
                  data-testid="cart-signin-cta"
                >
                  Sign in to earn loyalty rewards →
                </button>
              )}

              {items.length === 0 ? (
                <div className="text-cream/60 text-center py-16 font-heading uppercase tracking-widest" data-testid="cart-empty-msg">
                  Your basket is empty
                </div>
              ) : (
                <ul className="space-y-3">
                  {items.map((it) => (
                    <li key={it.line_id} className={`border p-3 ${it.reward_id ? "border-gold bg-gold/5" : "border-border"}`} data-testid={`cart-line-${it.line_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="font-heading uppercase text-cream tracking-tight text-lg leading-tight">
                            {it.name}
                          </div>
                          <div className={`text-sm mt-1 ${it.reward_id ? "text-gold" : "text-cream/60"}`}>
                            {it.reward_id ? "FREE — Loyalty Reward" : `£${it.unit_price.toFixed(2)}`}
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(it.line_id)}
                          className="text-cream/40 hover:text-accent"
                          data-testid={`cart-remove-${it.line_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {!it.reward_id && (
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center border border-border">
                            <button onClick={() => updateQty(it.line_id, -1)} className="px-2 py-1 hover:bg-secondary" data-testid={`cart-dec-${it.line_id}`}>
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-3 font-heading text-lg">{it.quantity}</span>
                            <button onClick={() => updateQty(it.line_id, 1)} className="px-2 py-1 hover:bg-secondary" data-testid={`cart-inc-${it.line_id}`}>
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="font-heading text-gold text-xl">
                            £{(it.unit_price * it.quantity).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-border px-6 py-5 space-y-1.5">
              <Row label="Subtotal" value={`£${subtotalPaid.toFixed(2)}`} testid="cart-subtotal" />
              <Row label="Delivery" value={subtotalPaid > 0 ? `£${deliveryFee.toFixed(2)}` : "—"} testid="cart-delivery-fee" />
              <div className="border-t border-border my-2" />
              <div className="flex items-baseline justify-between">
                <span className="font-heading uppercase tracking-widest text-cream text-sm">Total</span>
                <span className="font-heading text-gold text-3xl" data-testid="cart-total">£{total.toFixed(2)}</span>
              </div>

              {blockReason && (
                <div className="mt-3 inline-flex items-start gap-2 text-accent text-xs font-heading uppercase tracking-widest" data-testid="cart-block-reason">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{blockReason}</span>
                </div>
              )}

              <button
                disabled={blocked}
                onClick={handleCheckoutClick}
                className={`w-full btn-poster mt-3 ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="checkout-btn"
              >
                Checkout
              </button>
              <p className="mt-2 text-xs text-cream/50 text-center">
                Delivery: Partington · Carrington · Warburton
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Checkout Gate — sign in or continue as guest */}
      <Dialog open={gateOpen} onOpenChange={setGateOpen}>
        <DialogContent className="bg-black border-2 border-gold rounded-none max-w-md p-0" data-testid="checkout-gate">
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="font-heading text-cream uppercase tracking-tight text-3xl">
                Ready to Order?
              </DialogTitle>
              <DialogDescription className="text-cream/70 font-body">
                Sign in to earn loyalty rewards and save your details — or jump straight to checkout as a guest.
              </DialogDescription>
            </DialogHeader>

            {/* Guest option */}
            <button
              onClick={proceedAsGuest}
              className="mt-6 w-full flex items-center justify-between gap-3 px-4 py-4 border-2 border-border hover:border-gold/60 transition-colors group"
              data-testid="guest-checkout-btn"
            >
              <div className="text-left">
                <div className="font-heading uppercase tracking-tight text-cream text-lg leading-none flex items-center gap-2">
                  <ArrowRight className="h-5 w-5 text-gold" /> Continue as Guest
                </div>
                <div className="text-cream/50 text-xs mt-1">No account needed — just fill in your delivery details</div>
              </div>
            </button>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-cream/40 font-heading uppercase tracking-widest text-xs">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Login / Register tabs */}
            <div className="flex gap-1 border border-border w-fit">
              <button
                onClick={() => setGateTab("login")}
                className={`px-4 py-2 font-heading uppercase tracking-widest text-sm ${gateTab === "login" ? "bg-gold text-black" : "text-cream/70 hover:text-cream"}`}
                data-testid="gate-tab-login"
              >
                <LogIn className="inline h-4 w-4 mr-1" />Sign In
              </button>
              <button
                onClick={() => setGateTab("register")}
                className={`px-4 py-2 font-heading uppercase tracking-widest text-sm ${gateTab === "register" ? "bg-gold text-black" : "text-cream/70 hover:text-cream"}`}
                data-testid="gate-tab-register"
              >
                <UserCircle2 className="inline h-4 w-4 mr-1" />Register
              </button>
            </div>

            <form onSubmit={submitAuth} className="mt-4 space-y-3">
              {gateTab === "register" && (
                <>
                  <GateField label="Name *" value={gateForm.name} onChange={(v) => setF("name", v)} testid="gate-name" />
                  <GateField label="Phone *" value={gateForm.phone} onChange={(v) => setF("phone", v)} testid="gate-phone" />
                </>
              )}
              <GateField label="Email *" type="email" value={gateForm.email} onChange={(v) => setF("email", v)} testid="gate-email" />
              <GateField label="Password *" type="password" value={gateForm.password} onChange={(v) => setF("password", v)} testid="gate-password" />
              <button type="submit" disabled={gateSubmitting} className="w-full btn-poster mt-2" data-testid="gate-submit">
                {gateSubmitting ? <Loader2 className="h-5 w-5 animate-spin mr-2 inline" /> : null}
                {gateTab === "login" ? "Sign In & Checkout" : "Create Account & Checkout"}
              </button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} deliveryFee={deliveryFee} minOrder={minOrder} subtotalPaid={subtotalPaid} />
      <RewardRedeemDialog
        open={rewardDialog.open}
        onOpenChange={(v) => setRewardDialog((r) => ({ ...r, open: v }))}
        reward={rewardDialog.reward}
        pastaFlavours={pastaFlavours}
        wingFlavours={wingFlavours}
      />
    </>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="font-heading uppercase tracking-widest text-cream/60 text-xs">{label}</span>
      <span className="font-heading text-cream text-lg" data-testid={testid}>{value}</span>
    </div>
  );
}

function GateField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="font-heading uppercase tracking-widest text-xs text-cream/70">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border-2 border-border focus:border-gold outline-none px-3 py-2 text-cream"
        data-testid={testid}
        required
      />
    </div>
  );
}
